<script setup lang="ts">
import type { Ref } from 'vue'
import { computed, inject, ref, watch } from 'vue'
import type { ITicketCategoryTreeNode } from '../../types/settings/ITicketCategorySettings'
import { getRandomAvatar } from '@/utils/getRandomAvatar'

const props = withDefaults(
  defineProps<{
    node: ITicketCategoryTreeNode
    selectedNodeId: string | null
    collapsedNodes: Array<string>
    depth?: number
  }>(),
  {
    depth: 0,
  },
)

const emit = defineEmits<{
  'select': [node: ITicketCategoryTreeNode]
  'add-child': [node: ITicketCategoryTreeNode]
  'toggle-collapse': [id: string]
}>()

const isCompactModeActive = inject<Ref<boolean>>('isCompactModeActive', ref(false))
const isDragDropEnabled = inject<Ref<boolean>>('isDragDropEnabled', ref(false))
const onToggleNodeActive = inject<(node: ITicketCategoryTreeNode, isActive: boolean) => Promise<void> | void>(
  'onToggleNodeActive',
  () => {},
)
const isLocallyCompact = ref(isCompactModeActive.value)
const searchQuery = inject<Ref<string>>('searchQuery', ref(''))

const highlightMatch = (text: string, searchTerm: string) => {
  const query = (searchTerm || '').trim()
  if (!query || !text) {
    return text
  }
  const safe = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const re = new RegExp(`(${safe})`, 'gi')

  return text.replace(re, '<mark class="search-highlight">$1</mark>')
}

watch(isCompactModeActive, newVal => {
  isLocallyCompact.value = newVal
})

const isCompact = computed(() => isLocallyCompact.value)

function toggleCompactOnly() {
  isLocallyCompact.value = !isLocallyCompact.value
}

const currentDepth = computed(() => {
  const d = Number(props.depth)
  return isNaN(d) ? 0 : d
})

const hasChildren = computed(() => {
  return Array.isArray(props.node.children) && props.node.children.length > 0
})

const isCollapsed = computed(() => {
  return props.collapsedNodes.includes(props.node.id)
})

const manager = computed(() => props.node.responsible_employee || null)

const managerFullName = computed(() => {
  if (!manager.value) return ''
  if (manager.value.full_name) return manager.value.full_name
  const { last_name, first_name, middle_name } = manager.value
  return [last_name, first_name, middle_name].filter(Boolean).join(' ')
})

const draggedNodeId = inject<Ref<string | null>>('draggedNodeId')
const dragOverNodeId = inject<Ref<string | null>>('dragOverNodeId')
const draggedNode = inject<Ref<ITicketCategoryTreeNode | null>>('draggedNode')
const onNodeDragStart = inject<(node: ITicketCategoryTreeNode) => void>('onNodeDragStart')
const onNodeDragOver = inject<(node: ITicketCategoryTreeNode) => void>('onNodeDragOver')
const onNodeDragLeave = inject<() => void>('onNodeDragLeave')
const onNodeDrop = inject<(targetNode: ITicketCategoryTreeNode) => void>('onNodeDrop')
const onNodeDragEnd = inject<() => void>('onNodeDragEnd')

const draggedNodeName = computed(() => draggedNode?.value?.name || '')

const isCurrentDragOver = computed(() => {
  return dragOverNodeId ? dragOverNodeId.value === props.node.id : false
})

const isCurrentDragged = computed(() => {
  return draggedNodeId ? draggedNodeId.value === props.node.id : false
})

const hasDraggedNode = computed(() => {
  return draggedNodeId ? draggedNodeId.value !== null : false
})

/** Глобальные карточки всегда визуально приглушены */
const isVisuallyLocked = computed(() => !!props.node.is_daomed_main)

const isTogglingActive = ref(false)

async function handleToggleActive(value: boolean | null) {
  if (isTogglingActive.value) return
  isTogglingActive.value = true
  try {
    await onToggleNodeActive(props.node, Boolean(value))
  } finally {
    isTogglingActive.value = false
  }
}

function onActivityChipClick() {
  if (isTogglingActive.value) return
  void handleToggleActive(!(props.node.is_active !== false))
}

function handleDragStart(e: DragEvent, node: ITicketCategoryTreeNode) {
  if (!isDragDropEnabled.value || node.is_daomed_main) return
  onNodeDragStart?.(node)

  const target = e.currentTarget as HTMLElement
  if (target && e.dataTransfer) {
    try {
      target.classList.add('is-drag-active-ghost')
      e.dataTransfer?.setDragImage(target, 140, 32)
      setTimeout(() => {
        target.classList.remove('is-drag-active-ghost')
      }, 0)
    } catch (err) {
      console.error('[NODE] Error setting drag image:', err)
      target.classList.remove('is-drag-active-ghost')
    }
  }
}

const fallbackAvatars = new Map<string, string>()

function getAvatarUrl(mgr: any): string {
  if (!mgr) return getRandomAvatar()
  if (mgr.avatar) return mgr.avatar
  const id = String(mgr.id || 'default')
  if (!fallbackAvatars.has(id))
    fallbackAvatars.set(id, getRandomAvatar())
  return fallbackAvatars.get(id)!
}
</script>

<template>
  <li
    class="tree-node-li"
    :class="{
      'has-subdepartments': hasChildren && !isCollapsed,
      'is-daomed-locked-branch': isVisuallyLocked,
    }"
    :style="{ '--depth': currentDepth }"
  >
    <div
      class="tree-node-wrapper"
      :class="{ 'has-children': (hasChildren && !isCollapsed) || (isCurrentDragOver && hasDraggedNode) }"
    >
      <div
        :id="`dept-card-${node.id}`"
        class="dept-node-card"
        :class="{
          'selected': selectedNodeId === String(node.id),
          'is-dragging-origin': isCurrentDragged,
          'is-dragging-over': isCurrentDragged && dragOverNodeId !== null,
          'drag-over': isCurrentDragOver,
          'is-dragging-active': hasDraggedNode,
          'compact': isCompact,
          'is-daomed-locked': isVisuallyLocked,
        }"
        :draggable="isDragDropEnabled && !node.is_daomed_main"
        @dragstart="handleDragStart($event, node)"
        @dragover.prevent="onNodeDragOver && onNodeDragOver(node)"
        @dragleave="onNodeDragLeave && onNodeDragLeave()"
        @drop="onNodeDrop && onNodeDrop(node)"
        @dragend="onNodeDragEnd && onNodeDragEnd()"
      >
        <!-- Блок фантома переноса, который активируется по классу .is-drag-active-ghost -->
        <div class="custom-drag-ghost" style="display: none;">
          <div style="display: flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 6px; background: rgba(255, 255, 255, 0.12); flex-shrink: 0;">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ffffff"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M5 9l-3 3 3 3M9 5l3-3 3 3M15 19l-3 3-3-3M19 9l3 3-3 3M2 12h20M12 2v20" />
            </svg>
          </div>
          <div style="display: flex; flex-direction: column; overflow: hidden; flex-grow: 1; text-align: left;">
            <span style="font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.8px; color: rgba(255, 255, 255, 0.45); margin-bottom: 2px;">Перенос категории</span>
            <span style="font-size: 13px; font-weight: 600; color: #ffffff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
              {{ node.name }}
            </span>
          </div>
        </div>
        <!-- Шапка карточки отделения / категории -->
        <div
          class="d-flex flex-column px-4 py-3 border-b dept-header w-100"
          @click.stop="emit('select', node)"
        >
          <!-- Верхняя строка: Бейджи + Стрелочка -->
          <div class="d-flex align-center justify-space-between w-100 gap-2">
            <div class="d-flex flex-wrap align-center gap-1 min-width-0">
              <VChip
                v-if="node.is_daomed_main"
                size="x-small"
                color="primary"
                variant="tonal"
                class="font-weight-medium px-2 card-status-chip"
              >
                Глобальный
              </VChip>
              <VChip
                v-if="node.is_active !== undefined"
                size="x-small"
                :color="node.is_active !== false ? 'success' : 'secondary'"
                variant="tonal"
                class="font-weight-medium px-2 card-status-chip activity-chip cursor-pointer"
                @click.stop="onActivityChipClick"
              >
                <span>{{ node.is_active !== false ? 'Активна' : 'Неактивна' }}</span>
                <VSwitch
                  :model-value="node.is_active !== false"
                  color="success"
                  hide-details
                  density="compact"
                  inset
                  class="activity-chip-switch"
                  tabindex="-1"
                />
              </VChip>
            </div>
            <VIcon
              :icon="isCompact ? 'ri-arrow-down-s-line' : 'ri-arrow-up-s-line'"
              size="20"
              class="cursor-pointer text-medium-emphasis hover-primary flex-shrink-0"
              @click.stop="toggleCompactOnly"
            />
          </div>

          <!-- Нижняя строка: Название (со скроллом при переполнении) -->
          <div class="dept-title-scroll flex-grow-1 min-width-0">
            <span
              class="font-weight-bold text-high-emphasis"
              style="font-size: 14px; line-height: 1.3;"
              v-html="highlightMatch(node.name, searchQuery)"
            />
          </div>
        </div>

        <template v-if="!isCompact">
          <!-- Ответственный сотрудник -->
          <div
            v-if="manager"
            class="d-flex align-center gap-3 px-4 py-3 border-b dept-manager cursor-pointer-hover"
            @click="emit('select', node)"
          >
            <VAvatar size="42" class="border" :image="getAvatarUrl(manager)" />
            <div class="flex-grow-1 min-width-0">
              <div
                class="text-body-1 font-weight-bold text-truncate text-high-emphasis manager-name-text"
                v-html="highlightMatch(managerFullName, searchQuery)"
              />
              <div class="text-caption text-medium-emphasis text-truncate">
                {{ manager.position_name || 'Ответственный сотрудник' }}
              </div>
            </div>
          </div>
          <div
            v-else
            class="d-flex align-center gap-3 px-4 py-3 border-b dept-manager text-medium-emphasis"
            @click="emit('select', node)"
          >
            <VAvatar size="42" class="border d-flex align-center justify-center" style="background-color: rgba(var(--v-theme-on-surface), 0.04) !important;">
              <VIcon icon="ri-user-line" size="20" class="text-medium-emphasis" />
            </VAvatar>
            <div class="flex-grow-1 min-width-0">
              <div class="text-body-2 font-weight-medium text-high-emphasis">
                Ответственный сотрудник
              </div>
              <div class="text-caption text-medium-emphasis font-weight-medium">
                Не назначен
              </div>
            </div>
          </div>

          <!-- Детали категории -->
          <div
            class="d-flex flex-column px-4 py-3 gap-2 dept-details"
            style="font-size: 12px; flex-grow: 1;"
            @click="emit('select', node)"
          >
            <div class="d-flex align-center justify-space-between">
              <span class="text-caption text-medium-emphasis font-weight-medium">Служба:</span>
              <span
                v-if="node.service?.name || node.service?.short_name"
                class="text-body-2 font-weight-medium text-high-emphasis text-truncate"
                style="max-width: 200px;"
                v-html="highlightMatch(node.service.short_name || node.service.name, searchQuery)"
              />
              <span v-else class="text-body-2 text-high-emphasis font-weight-medium">
                Не указана
              </span>
            </div>
            <div class="d-flex align-center justify-space-between">
              <span class="text-caption text-medium-emphasis font-weight-medium">Группа:</span>
              <span
                v-if="node.work_group"
                class="text-body-2 font-weight-medium text-high-emphasis text-truncate"
                style="max-width: 200px;"
                v-html="highlightMatch(node.work_group.name, searchQuery)"
              />
              <span v-else class="text-body-2 text-high-emphasis font-weight-medium">
                Не назначена
              </span>
            </div>
            <div class="d-flex align-center justify-space-between">
              <span class="text-caption text-medium-emphasis font-weight-medium">Описание:</span>
              <span
                v-if="node.description"
                class="text-body-2 font-weight-medium text-high-emphasis text-truncate"
                style="max-width: 200px;"
                v-html="highlightMatch(node.description, searchQuery)"
              />
              <span v-else class="text-body-2 text-high-emphasis font-weight-medium">
                Не указано
              </span>
            </div>
          </div>
        </template>

        <!-- Нижняя кнопка-плашка для раскрытия детей / drop zone -->
        <div
          v-if="isCurrentDragOver"
          class="bottom-action-btn drag-over-active d-flex align-center justify-center py-2 text-primary w-100"
          style="background-color: rgba(var(--v-theme-primary), 0.15) !important;"
        >
          <VIcon icon="ri-arrow-down-double-line" size="16" class="mr-1 text-primary animate-bounce-down" />
          <span class="text-caption font-weight-bold text-primary">
            Сделать подкатегорией
          </span>
        </div>
        <div
          v-else-if="hasChildren"
          class="bottom-action-btn d-flex align-center justify-center py-2"
          :class="{ expanded: !isCollapsed }"
          @click.stop="emit('toggle-collapse', node.id)"
        >
          <span class="text-caption font-weight-bold mr-1">{{ node.children?.length || 0 }} подкатегорий</span>
          <VIcon :icon="isCollapsed ? 'ri-arrow-down-s-line' : 'ri-arrow-up-s-line'" size="16" />
        </div>
        <div
          v-else
          class="bottom-action-btn disabled d-flex align-center justify-center py-2 text-medium-emphasis"
        >
          <span class="text-caption font-weight-medium">Нет подкатегорий</span>
        </div>
      </div>
    </div>

    <!-- Рекурсивный рендеринг дочерних категорий -->
    <ul v-if="(hasChildren && !isCollapsed) || (dragOverNodeId === node.id && draggedNodeId !== null)">
      <template v-if="hasChildren && !isCollapsed">
        <TicketCategoryTreeNode
          v-for="sub in node.children"
          :key="sub.id"
          :node="sub"
          :selected-node-id="selectedNodeId"
          :collapsed-nodes="collapsedNodes"
          :depth="currentDepth + 1"
          @select="n => $emit('select', n)"
          @add-child="n => $emit('add-child', n)"
          @toggle-collapse="id => $emit('toggle-collapse', id)"
        />
      </template>

      <!-- Временное превью перетаскиваемой карточки как дочерней -->
      <li v-if="dragOverNodeId === node.id && draggedNodeId !== null" class="preview-node-li" :style="{ '--depth': currentDepth + 1 }">
        <div class="tree-node-wrapper">
          <div class="dept-node-card preview-card">
            <div class="d-flex align-center gap-2 px-4 py-2 border-b w-100 justify-center" style="background: rgba(var(--v-theme-primary), 0.04); border-color: rgba(var(--v-theme-primary), 0.12) !important;">
              <VIcon icon="ri-git-branch-line" size="16" class="text-primary" />
              <span class="font-weight-bold text-truncate text-primary" style="font-size: 13px; max-width: 240px;">
                {{ draggedNodeName }}
              </span>
            </div>
            <div class="d-flex align-center justify-center flex-grow-1 w-100 py-2">
              <span class="text-caption text-medium-emphasis font-weight-bold d-flex align-center gap-1">
                <VIcon icon="ri-arrow-down-double-line" size="14" class="text-primary animate-bounce-down" />
                Станет подкатегорией
              </span>
            </div>
          </div>
        </div>
      </li>
    </ul>
  </li>
</template>

<style scoped lang="scss">
.preview-node-li {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding-top: 32px;
  list-style-type: none;
  flex-shrink: 0;
  width: min-content;
}

.preview-card {
  width: 320px !important;
  height: 120px !important;
  border: 2px dashed rgb(var(--v-theme-primary)) !important;
  background-color: rgba(var(--v-theme-primary), 0.04) !important;
  box-shadow: 0 4px 12px rgba(var(--v-theme-primary), 0.15) !important;
  opacity: 0.85;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transform: scale(0.95);
  animation: pulsePreview 1.8s infinite ease-in-out;
}

@keyframes pulsePreview {
  0%, 100% {
    opacity: 0.7;
    transform: scale(0.93) translateY(0);
    box-shadow: 0 4px 10px rgba(var(--v-theme-primary), 0.1);
  }
  50% {
    opacity: 0.95;
    transform: scale(0.97) translateY(-2px);
    box-shadow: 0 8px 20px rgba(var(--v-theme-primary), 0.25);
  }
}

.custom-drag-ghost {
  display: none;
}

.dept-node-card.is-drag-active-ghost {
  width: 280px !important;
  height: 64px !important;
  min-height: 64px !important;
  max-height: 64px !important;
  background: #2f273e !important;
  border: 1.5px solid rgb(138, 92, 246) !important;
  border-radius: 8px !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4) !important;
  color: #ffffff !important;
  font-family: 'Inter', sans-serif !important;
  box-sizing: border-box !important;

  & > :not(.custom-drag-ghost) {
    display: none !important;
  }

  .custom-drag-ghost {
    display: flex !important;
    align-items: center;
    gap: 10px;
    width: 100%;
    height: 100%;
    padding: 10px 14px;
    box-sizing: border-box;
  }
}

.dept-node-card.is-dragging-active * {
  pointer-events: none !important;
}

.dept-node-card.is-daomed-locked {
  opacity: 0.82;
}

.dept-node-card.is-daomed-locked:hover {
  opacity: 0.92;
}

.card-status-chip {
  height: 22px !important;
  min-height: 22px !important;
}

.activity-chip {
  padding-inline: 6px 4px !important;
  overflow: hidden;

  :deep(.v-chip__content) {
    height: 22px;
    max-height: 22px;
    gap: 2px;
    overflow: hidden;
  }
}

.activity-chip-switch {
  margin-inline: 0 !important;
  flex: 0 0 auto;
  width: 26px;
  height: 16px;
  overflow: hidden;
  transform: scale(0.65);
  transform-origin: center center;
  pointer-events: none;

  :deep(.v-input),
  :deep(.v-input__control),
  :deep(.v-selection-control) {
    min-height: 0 !important;
    height: 16px;
    width: 28px;
  }

  :deep(.v-selection-control__wrapper) {
    height: 14px;
    width: 26px;
  }

  :deep(.v-switch__track) {
    opacity: 1;
    height: 12px !important;
    width: 24px !important;
    min-width: 24px !important;
  }

  :deep(.v-switch__thumb) {
    height: 8px !important;
    width: 8px !important;
  }
}

.hover-primary {
  transition: color 0.15s ease-in-out;
  &:hover {
    color: rgb(var(--v-theme-primary)) !important;
  }
}

:deep(.search-highlight) {
  background-color: rgb(var(--v-theme-primary-500), var(--v-activated-opacity)) !important;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity)) !important;
  padding: 2px 0;
  border-radius: 2px;
}

:deep(.v-theme--dark .search-highlight) {
  background-color: rgba(var(--v-theme-primary-500), 0.38) !important;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity)) !important;
  padding: 2px 0;
  border-radius: 2px;
}
</style>
